import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'

import {
    archiveThirdPartyRentalAssetOffering,
    createThirdPartyRentalAssetOffering,
    listThirdPartyRentalAssetOfferings,
    unarchiveThirdPartyRentalAssetOffering,
    updateThirdPartyRentalAssetOffering,
} from './api'
import {rentalAssetKeys} from './query-keys'
import type {
    CreateThirdPartyRentalAssetOfferingInput,
    ListThirdPartyRentalAssetOfferingsOptions,
    UpdateThirdPartyRentalAssetOfferingInput,
} from './schemas'

export function useThirdPartyRentalAssetOfferings(
    options: ListThirdPartyRentalAssetOfferingsOptions = {},
    queryOptions?: {enabled?: boolean},
) {
    return useQuery({
        queryKey: rentalAssetKeys.offerings(options),
        queryFn: () => listThirdPartyRentalAssetOfferings(options),
        enabled: queryOptions?.enabled ?? true,
    })
}

export function useCreateThirdPartyRentalAssetOffering() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: CreateThirdPartyRentalAssetOfferingInput) =>
            createThirdPartyRentalAssetOffering(input),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: ['third-party-rental-asset-offerings'],
            })
        },
    })
}

export function useUpdateThirdPartyRentalAssetOffering() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({id, input}: {id: string; input: UpdateThirdPartyRentalAssetOfferingInput}) =>
            updateThirdPartyRentalAssetOffering(id, input),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: ['third-party-rental-asset-offerings'],
            })
        },
    })
}

export function useArchiveThirdPartyRentalAssetOffering() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (id: string) => archiveThirdPartyRentalAssetOffering(id),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: ['third-party-rental-asset-offerings'],
            })
        },
    })
}

export function useUnarchiveThirdPartyRentalAssetOffering() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (id: string) => unarchiveThirdPartyRentalAssetOffering(id),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: ['third-party-rental-asset-offerings'],
            })
        },
    })
}
